/* globals mixpanel */

import moment from 'moment';
import {Component} from 'panel';

import {apiClient} from 'common/util/api-client';
import {registerMPElement} from 'common/util/register-element';

import {CHANGE_TYPE_REMOVED, CHANGE_TYPE_SENT_FEEDBACK} from '../constants';
import {generateMessage} from '../utils/message';
import {
  getAlertDetailUrl,
  getAlertDirection,
  getAlertUrl,
  getSentFeedbackAtMs,
  reportType,
  setSentFeedbackAtMs,
} from '../utils/common';
import {buildGlobalChangedAlert, buildGlobalChangeHandler} from '../utils/globalEvents.js';
import {trackingProperties} from '../utils/tracking';
import {generatePermalink} from '../utils/permalink';

import template from './index.jade';
import css from './index.styl?inline';

class SmartHubAlert extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      alert: `json`,
      'display-context': `string`,
      'project-id': `number`,
      'utc-offset': `number`,
      visible: `boolean`,
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        projectId: null,
        displayContext: null,
        alert: null,
        alertId: null,
        stopFeedbackAnimation: false,
        type: null,
        isRead: false,
        isAlertOwner: false,
        isUseful: false,
        message: null,
        utcOffset: 0,
      },
      helpers: {
        isFeedbackDisabled: () => {
          return !!getSentFeedbackAtMs(this.state.alert);
        },
        generatePermalink: () => {
          return generatePermalink({
            alert: this.state.alert,
            projectId: this.state.projectId,
            workspaceId: this.attr(`workspace-id`),
          });
        },
        getReportType: () => reportType(this.state.alert),
        getAlertTime: () => {
          const {anomaly, contour} = this.state.alert;
          if (anomaly) {
            const {anomalyDetail, anomalyDetails} = anomaly;
            const anomalyTimestamp = anomalyDetail
              ? anomalyDetail.anomalyTimestamp
              : anomalyDetails[0].anomalyTimestamp;
            return this.formatAlertDate(Number(anomalyTimestamp));
          } else if (contour) {
            return this.formatAlertDate(Number(this.state.alert.ingestedAtMs));
          }
        },
        getAlertDirection: () => getAlertDirection(this.state.alert),
        handleClickAlert: (ev) => {
          // Don't propagate any clicks on the alert, since custom events are fired for different interactions
          ev.stopPropagation();
        },
        handleSentFeedback: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          const {isUseful, sentFeedbackAtMs} = ev.detail;

          const params = {
            is_useful: isUseful, // eslint-disable-line camelcase
            display_context: this.state.displayContext, // eslint-disable-line camelcase
          };
          this.fetch(`PATCH`, params);

          this.trackEvent(`Smart Hub: Sent Feedback`, {is_useful: isUseful});

          this.setSentFeedbackAtMs(sentFeedbackAtMs);

          this.dispatchEvent(buildGlobalChangedAlert(CHANGE_TYPE_SENT_FEEDBACK, this.state.alert, {isUseful}));
        },
        handleClickLink: (ev) => {
          ev.stopPropagation();

          const modifierKey = ev.ctrlKey || ev.metaKey;
          if (!modifierKey) {
            ev.preventDefault();
          }

          const href = ev.currentTarget.href;
          const navigateToLinkIfNeeded = () => {
            if (!modifierKey) {
              window.location.href = href;
            }
          };

          Promise.all([
            this.incrementPeopleProperty(`Smart Hub: Total Alerts Clicked`),
            this.trackEvent(`Smart Hub: Clicked Alert Link`),
          ]).then(navigateToLinkIfNeeded);

          // Navigate to link if the mixpanel track calls doesn't finish within 300ms.
          setTimeout(navigateToLinkIfNeeded, 300);

          this.dispatchEvent(
            new CustomEvent(`clickedLink`, {
              detail: {modifierKey},
            }),
          );
        },
        handleRemoveAlert: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          if (this.state.displayContext !== `IN_REPORT`) {
            this.fetch(`DELETE`);
            this.trackEvent(`Smart Hub: Deleted Alert`);
          }
          this.dispatchEvent(buildGlobalChangedAlert(CHANGE_TYPE_REMOVED, this.state.alert));
        },
        showFeedback: () => this.state.isAlertOwner && !this.state.stopFeedbackAnimation,
      },
    };
  }

  // Only used in tests to inject spy dependency
  set appApi(value) {
    this._appApi = value;
  }

  get appApi() {
    return this._appApi || apiClient;
  }

  formatAlertDate(timestampMs) {
    return moment(timestampMs)
      .utcOffset(this.state.utcOffset || 0)
      .format(`ddd, MMM D`);
  }

  updateAlertState() {
    const alert = this.attr(`alert`);
    this.update({
      alert,
      projectId: this.attr(`project-id`),
      displayContext: this.attr(`display-context`),
      // The caller can specify the value to use the alert's message rather than using the value in the alert.
      // The value in the alert can be used for alerts in the smart hub dropdown. When used in-report, let the
      // report pass the value based on the current data in the report (since the value in the alert could be
      // out of date).
      utcOffset: this.attr(`utc-offset`),
    });

    if (!alert) {
      return;
    }

    // TODO(ML-974): Most of this data is immutable and doesn't need to live in state.
    this.update({
      alertId: alert.alertId,
      isRead: alert.isRead,
      isAlertOwner: alert.isAlertOwner,
      message: generateMessage(alert, {
        displayContext: this.state.displayContext,
        utcOffset: this.state.utcOffset,
      }),
    });
  }

  connectedCallback() {
    this.updateAlertState();

    super.connectedCallback(...arguments);

    this._handleGlobalChangedAlert = buildGlobalChangeHandler(this, this.state.alert);
    document.addEventListener(`globalChangedAlert`, this._handleGlobalChangedAlert);
  }

  stopFeedbackAnimationIfNeeded() {
    // unmount the feedback button component
    if (getSentFeedbackAtMs(this.state.alert)) {
      this.update({stopFeedbackAnimation: true});
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback(...arguments);
    document.removeEventListener(`globalChangedAlert`, this._handleGlobalChangedAlert);
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);
    if (!this.initialized) {
      return;
    }
    if (attr === `visible`) {
      this.stopFeedbackAnimationIfNeeded();
    }
    this.updateAlertState();
  }

  _fetchUrl() {
    const {alert, projectId} = this.state;

    const detailId = (() => {
      const {anomaly, contour} = alert;
      if (anomaly && anomaly.anomalyDetail) {
        return anomaly.anomalyDetail.anomalyDetailId;
      } else if (contour && contour.contourDetail) {
        return contour.contourDetail.contourDetailId;
      } else {
        return null;
      }
    })();

    if (detailId) {
      return getAlertDetailUrl({projectId, alertId: alert.alertId, detailId});
    } else {
      return getAlertUrl({projectId, alertId: alert.alertId, detailId});
    }
  }

  fetch(method, params) {
    return this.appApi
      .fetch(this._fetchUrl(), {method, body: JSON.stringify(params)})
      .then((response) => response.json());
  }

  setSentFeedbackAtMs(ms) {
    const {alert} = this.state;
    setSentFeedbackAtMs(alert, ms);
    this.setAttribute(`alert`, JSON.stringify(alert));
  }

  trackEvent(eventName, extraProps = {}) {
    const commonProperties = trackingProperties(this.state.alert);

    return new Promise((resolve) => {
      mixpanel.track(
        eventName,
        Object.assign(
          {
            display_context: this.state.displayContext.toUpperCase(),
            platform_created: `WEB`,
            platform_detected: `WEB`,
            project_id: this.state.projectId,
            alert_version: `1`,
            has_explains: false,
          },
          commonProperties,
          extraProps,
        ),
        resolve,
      );
    });
  }

  incrementPeopleProperty(propertyName) {
    return new Promise((resolve) => {
      mixpanel.people.increment(propertyName, 1, resolve);
    });
  }
}

export default registerMPElement(`mp-smart-hub-alert`, SmartHubAlert);
